// Swedish policy content for the Redoc AML & KYC landing page.
// Written to reasonably reflect what actually happens on the site:
//   - contact / demo / order forms saved to MongoDB
//   - notification emails sent via SendGrid / Resend to info@amlkyc.se
//   - payments handled by Stripe Checkout (external redirect)
//   - basic analytics / functional cookies

export const LEGAL_UPDATED = "2026-07-05";

export const villkorSections = [
  {
    heading: "1. Allmänt",
    body:
      "Dessa användarvillkor (”Villkoren”) gäller för besök på amlkyc.se (”Webbplatsen”) samt för de tjänster och produkter som Redoc TM Aktiebolag (”Redoc”, ”vi”, ”oss”) tillhandahåller under varumärket Redoc AML & KYC. Genom att använda Webbplatsen accepterar Du dessa Villkor.",
  },
  {
    heading: "2. Om Webbplatsen",
    body:
      "Webbplatsen beskriver Redocs programvara och tillhörande tjänster för allmän riskbedömning, kundkännedom (KYC) och kvalitetssäkring enligt Penningtvättlagen. Innehållet är av informativ karaktär och utgör inte juridisk eller redovisningsteknisk rådgivning i det enskilda fallet.",
  },
  {
    heading: "3. Beställning och betalning",
    body:
      "Beställning av programvaran eller tjänsterna sker via beställningsformuläret på Webbplatsen. När Du klickar ”Fortsätt till säker betalning” omdirigeras Du till Stripes betalningsportal där själva betalningen och insamlingen av betalningsuppgifter sker. Vi tar inte emot eller lagrar kort- eller bankuppgifter på Webbplatsen.\n\nAngivna priser är i svenska kronor och exklusive moms om inte annat framgår.",
  },
  {
    heading: "4. Avtal och leverans",
    body:
      "Ett bindande avtal ingås först när Din betalning har genomförts via Stripe och vi har skickat en orderbekräftelse till den e-postadress Du angett. Redoc AML & KYC är en webbaserad tjänst och levereras genom att inloggningsuppgifter skickas via e-post till beställaren.",
  },
  {
    heading: "5. Ångerrätt",
    body:
      "Vid distansavtal med konsument gäller konsumentskyddande lagstiftning. För företagskunder tillämpas ingen lagstadgad ångerrätt. Kontakta oss om Du är osäker på vad som gäller för Din beställning.",
  },
  {
    heading: "6. Ansvarsbegränsning",
    body:
      "Webbplatsen och dess innehåll tillhandahålls i befintligt skick. Redoc ansvarar inte för indirekta skador eller följdskador som kan uppkomma vid användning av Webbplatsen eller dess innehåll. Ansvar för fel eller brist i tjänsten regleras i särskilt avtal mellan Redoc och kund.",
  },
  {
    heading: "7. Immateriella rättigheter",
    body:
      "Allt material på Webbplatsen – texter, grafik, logotyper, mallar och programvara – tillhör Redoc TM Aktiebolag eller dess licensgivare. Materialet får inte kopieras, distribueras eller användas kommersiellt utan skriftligt medgivande.",
  },
  {
    heading: "8. Ändringar",
    body:
      "Redoc kan komma att uppdatera Villkoren från tid till annan. Den senaste versionen publiceras alltid på Webbplatsen.",
  },
  {
    heading: "9. Tillämplig lag och tvist",
    body:
      "Svensk lag tillämpas på Villkoren. Tvist ska i första hand lösas genom förhandling och därefter avgöras av svensk allmän domstol med Stockholms tingsrätt som första instans.",
  },
  {
    heading: "10. Kontakt",
    body:
      "Frågor om Villkoren besvaras via kontaktformuläret på amlkyc.se.",
  },
];

export const integritetSections = [
  {
    heading: "1. Personuppgiftsansvarig",
    body:
      "Redoc TM Aktiebolag är personuppgiftsansvarig för de personuppgifter som samlas in via amlkyc.se. Kontakt sker via kontaktformuläret på Webbplatsen.",
  },
  {
    heading: "2. Vilka uppgifter vi behandlar",
    body:
      "När Du använder våra formulär för att beställa, boka demo eller begära information behandlar vi de uppgifter Du själv lämnar:",
    list: [
      "Namn",
      "Företag / byrå och eventuellt organisationsnummer",
      "E-postadress",
      "Telefonnummer",
      "Meddelande och önskat kontaktval (t.ex. demo eller information)",
      "Önskat datum för demo",
      "Vald produkt / tjänst vid beställning",
    ],
  },
  {
    heading: "3. Ändamål och rättslig grund",
    body:
      "Vi behandlar Dina personuppgifter för att kunna:",
    list: [
      "Besvara Din förfrågan om demo eller information (berättigat intresse och/eller för att vidta åtgärder inför avtal).",
      "Fullgöra beställning och skicka betalnings- och orderinformation (avtal).",
      "Uppfylla legala krav, t.ex. bokförings- och skatteregler (rättslig förpliktelse).",
      "Förbättra och säkra Webbplatsen (berättigat intresse).",
    ],
  },
  {
    heading: "4. Mottagare av personuppgifter",
    body:
      "Uppgifterna behandlas av oss och av våra personuppgiftsbiträden:",
    list: [
      "Vår drift- och e-postleverantör (Resend / SendGrid) för att skicka notifieringar och orderbekräftelser.",
      "Stripe, som betalleverantör vid beställning. Vid betalning omdirigeras Du till Stripes egen sida där Stripes integritetspolicy tillämpas.",
      "Vår databasleverantör som lagrar formulärinlämningar på servrar i EU/Sverige.",
    ],
  },
  {
    heading: "5. Överföring utanför EU/EES",
    body:
      "Vi eftersträvar att data behandlas inom EU/EES. Om personuppgifter i något led överförs utanför EU/EES sker det med lämpliga skyddsåtgärder (t.ex. EU-kommissionens standardavtalsklausuler).",
  },
  {
    heading: "6. Lagringstid",
    body:
      "Uppgifter från kontakt- och demoformulär sparas så länge det behövs för att hantera Din förfrågan, dock som utgångspunkt högst 24 månader. Uppgifter kopplade till en beställning sparas så länge det krävs enligt gällande bokföringslag.",
  },
  {
    heading: "7. Dina rättigheter",
    body:
      "Enligt dataskyddsförordningen (GDPR) har Du rätt att:",
    list: [
      "Begära information om, och kopia av, de personuppgifter vi behandlar om Dig.",
      "Begära rättelse av felaktiga uppgifter.",
      "Begära radering eller begränsning av behandlingen, i den mån det är förenligt med lagkrav och avtal.",
      "Invända mot behandling som sker med stöd av berättigat intresse.",
      "Klaga hos Integritetsskyddsmyndigheten (imy.se).",
    ],
  },
  {
    heading: "8. Säkerhet",
    body:
      "Vi vidtar tekniska och organisatoriska åtgärder för att skydda dina uppgifter, bl.a. TLS-kryptering vid överföring och begränsad behörighet till våra system.",
  },
  {
    heading: "9. Kontakt",
    body:
      "Du kan kontakta oss i integritetsfrågor via kontaktformuläret på amlkyc.se.",
  },
];

export const cookieSections = [
  {
    heading: "1. Vad är cookies?",
    body:
      "Cookies är små textfiler som lagras på din enhet när Du besöker en webbplats. De används bland annat för att webbplatsen ska fungera, för att komma ihåg dina val och för att mäta hur webbplatsen används.",
  },
  {
    heading: "2. Cookies vi använder på amlkyc.se",
    body:
      "På Webbplatsen används följande kategorier av cookies:",
    list: [
      "Nödvändiga cookies – krävs för att grundläggande funktioner ska fungera, t.ex. att skicka formulär.",
      "Funktionella cookies – kommer ihåg val och inställningar (t.ex. valt kontaktalternativ) under besöket.",
      "Statistik / analys – hjälper oss förstå hur besökare interagerar med Webbplatsen, i aggregerad och anonymiserad form.",
      "Cookies från tredje part – kan förekomma om Du klickar Dig vidare till t.ex. Stripe (för betalning) eller om vi använder Googles typsnitt/fontleverantör.",
    ],
  },
  {
    heading: "3. Samtycke och hantering",
    body:
      "Nödvändiga cookies används alltid för att Webbplatsen ska fungera. Övriga cookies används endast i den utsträckning gällande lagstiftning tillåter. Du kan alltid själv hantera cookies via inställningarna i Din webbläsare, t.ex. genom att blockera, begränsa eller radera cookies.",
  },
  {
    heading: "4. Konsekvenser av att blockera cookies",
    body:
      "Om Du väljer att blockera cookies kan vissa funktioner på Webbplatsen sluta fungera eller fungera sämre – t.ex. att skicka formulär, se dynamiskt innehåll eller genomföra betalning via Stripe.",
  },
  {
    heading: "5. Kontakt",
    body:
      "Har Du frågor om vår användning av cookies? Kontakta oss via kontaktformuläret på amlkyc.se.",
  },
];
